import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Avatar } from "@mui/material";
import { InboxOutlined as DefaultEmptyIcon } from "@mui/icons-material";
import { colors, customShadows, borderRadiusPx } from "../../../../theme";
import CommonButton from "../inputs/CommonButton";

/**
 * NoData / CommonEmptyState Component
 * Centralized, reusable empty state presentation adhering to AutoBET design tokens.
 */
const NoData = ({
  title = "No Data !",
  subtitle,
  description,
  icon,
  actionText,
  onAction,
  action,
  paper = true,
  minHeight = 260,
  maxWidth = 460,
  sx = {},
  cardSx = {},
}) => {
  const displaySubtitle = description || subtitle;

  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        p: { xs: 3, sm: 5 },
        width: "100%",
        maxWidth: maxWidth,
        mx: "auto",
        ...cardSx,
      }}
    >
      <Avatar
        sx={{
          width: 64,
          height: 64,
          bgcolor: colors.blue[50],
          color: colors.primary.main,
          mb: 2,
        }}
      >
        {icon || <DefaultEmptyIcon sx={{ fontSize: 32 }} />}
      </Avatar>

      {title && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: colors.slate[800],
            mb: displaySubtitle ? 0.75 : 1.5,
          }}
        >
          {title}
        </Typography>
      )}

      {displaySubtitle && (
        <Typography
          variant="body2"
          sx={{
            color: colors.slate[500],
            lineHeight: 1.6,
            mb: actionText || action ? 2.5 : 0,
            fontSize: "0.875rem",
          }}
        >
          {displaySubtitle}
        </Typography>
      )}

      {action ? (
        action
      ) : actionText && onAction ? (
        <CommonButton variant="primary" size="small" onClick={onAction}>
          {actionText}
        </CommonButton>
      ) : null}
    </Box>
  );

  if (!paper) {
    return (
      <Box
        sx={{
          minHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          ...sx,
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: colors.background.paper,
        borderRadius: borderRadiusPx.xl,
        boxShadow: customShadows.card,
        border: `1px solid ${colors.dashboard.cardBorder || colors.slate[200]}`,
        ...sx,
      }}
    >
      {content}
    </Box>
  );
};

NoData.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  description: PropTypes.node,
  icon: PropTypes.node,
  actionText: PropTypes.string,
  onAction: PropTypes.func,
  action: PropTypes.node,
  paper: PropTypes.bool,
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sx: PropTypes.object,
  cardSx: PropTypes.object,
};

export default NoData;
